import { FunnelIcon, SidebarSimpleIcon } from "@phosphor-icons/react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import NewsFeedFilterContainer from "@/components/news-feed/news-feed-filters/NewsFeedFilterContainer";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { ResizablePanelGroup } from "@/components/ui/resizable.tsx";
import { ResizeHandle } from "@/components/ui/resize-handle";
import { useFilterMaxWidth } from "@/hooks/useFilterMaxWidth";
import { useResizeHandle } from "@/hooks/useResizeHandle";
import { cn } from "@/lib/utils";

const MIN_FILTER_WIDTH = 255;
const COLLAPSED_WIDTH = 55;
/** Cap for filter sidebar width as a fraction of the viewport (see {@link useFilterMaxWidth}). */
const MAX_VIEWPORT_FRACTION = 0.4;
const LARGE_SCREEN_BREAKPOINT = 1024; // md breakpoint

interface NewsFeedFilterSidebarProps {
  /** When provided, called when user collapses the sidebar (e.g. to sync with route state). */
  onCollapse?: () => void;
}

const NewsFeedFilterSidebar = ({
  onCollapse,
}: NewsFeedFilterSidebarProps = {}) => {
  const [width, setWidth] = useState(COLLAPSED_WIDTH);
  const [isExpanded, setIsExpanded] = useState(false);
  const hasInitialRender = useRef(false);
  const widthRef = useRef(width);
  const isExpandedRef = useRef(isExpanded);

  // Keep refs in sync with state
  useEffect(() => {
    widthRef.current = width;
  }, [width]);

  useEffect(() => {
    isExpandedRef.current = isExpanded;
  }, [isExpanded]);

  // Track resize state locally to avoid circular dependency
  const [isResizing, setIsResizing] = useState(false);

  // Use the hook to calculate max width based on filter labels
  const { maxWidth, filterContainerRef, recalculateMaxWidth } =
    useFilterMaxWidth({
      isExpanded,
      isResizing,
      maxViewportPercentage: MAX_VIEWPORT_FRACTION,
    });

  const { handleMouseDown, isResizing: resizeHandleIsResizing } =
    useResizeHandle({
      minWidth: MIN_FILTER_WIDTH,
      maxWidth: maxWidth || window.innerWidth * MAX_VIEWPORT_FRACTION,
      getCurrentWidth: () => width,
      onWidthChange: setWidth,
    });

  // Sync resize state from resize handle
  useEffect(() => {
    setIsResizing(resizeHandleIsResizing);
  }, [resizeHandleIsResizing]);

  const hasMountedRef = useRef(false);

  useEffect(() => {
    // Only run once on mount
    if (hasMountedRef.current) return;

    // Check if screen is large enough on mount
    const isLargeScreen = window.innerWidth >= LARGE_SCREEN_BREAKPOINT;
    if (isLargeScreen) {
      // Always start with min width
      setWidth(MIN_FILTER_WIDTH);
      setIsExpanded(true);
      hasMountedRef.current = true;
      // Calculate max width after DOM is rendered
      setTimeout(() => {
        recalculateMaxWidth();
      }, 200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Handle window resize to collapse sidebar on smaller screens
  useEffect(() => {
    const handleResize = () => {
      const isLargeScreen = window.innerWidth >= LARGE_SCREEN_BREAKPOINT;
      const currentIsExpanded = isExpandedRef.current;
      const currentWidth = widthRef.current;

      if (!isLargeScreen && currentIsExpanded) {
        setWidth(COLLAPSED_WIDTH);
        setIsExpanded(false);
      }
      // Constrain current width to new max width if needed
      if (currentIsExpanded && maxWidth > 0 && currentWidth > maxWidth) {
        const constrainedWidth = Math.max(
          MIN_FILTER_WIDTH,
          Math.min(maxWidth, currentWidth),
        );
        setWidth(constrainedWidth);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [maxWidth]);

  // Skip initial animation after first render
  useLayoutEffect(() => {
    hasInitialRender.current = true;
  }, []);

  const handleToggle = () => {
    if (isExpanded) {
      // On collapse, reset to collapsed width
      setWidth(COLLAPSED_WIDTH);
      setIsExpanded(false);
    } else {
      // On expand, always start with min width
      setWidth(MIN_FILTER_WIDTH);
      setIsExpanded(true);
      // Calculate max width after expansion
      setTimeout(() => {
        recalculateMaxWidth();
      }, 100);
    }
  };

  // If collapsed, render as floating button
  if (!isExpanded) {
    return (
      <div className="pt-4 px-4">
        <Button
          onPress={handleToggle}
          variant={"outline"}
          className={
            "flex items-center gap-1 rounded-3xl shadow-[0_0_25px_0_rgba(105,29,211,0.10)]"
          }
          size={"sm"}
        >
          <SidebarSimpleIcon className={"w-4 h-4 text-gray-500"} />
          Show filters
        </Button>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "relative h-full min-h-0 w-full",
        isResizing && "select-none",
        "transition-all duration-200 ease-in-out",
      )}
      style={{ width }}
    >
      <div
        className={cn(
          "overflow-auto h-full relative border-r border-slate-100",
          "scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent hover:scrollbar-thumb-slate-300",
        )}
      >
        <div className="sticky top-0 bg-white flex items-center justify-between px-1.5 py-1 z-20">
          <div className="flex items-center gap-2">
            <FunnelIcon size={12} className="text-gray-600" />
            <span className="text-[10px] text-gray-600 uppercase font-medium">
              Filters
            </span>
          </div>
          <IconButton
            icon={<SidebarSimpleIcon size={16} />}
            variant="ghost"
            size="md"
            onClick={(e) => {
              e.stopPropagation();
              onCollapse?.();
              handleToggle();
            }}
            aria-label="Collapse filters"
            className="ml-auto text-gray-600 z-30"
          />
        </div>
        <div ref={filterContainerRef} className="h-[calc(100%-52px)]">
          <ResizablePanelGroup direction="vertical" className="h-full">
            <NewsFeedFilterContainer />
          </ResizablePanelGroup>
        </div>
      </div>
      <div className="hidden md:block">
        <ResizeHandle onMouseDown={handleMouseDown} className="z-20" />
      </div>
    </div>
  );
};

export default NewsFeedFilterSidebar;
